import { Field, Form, Formik } from "formik";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { shallow } from "zustand/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import Input from "../../components/form/Input";
import ItemSearchInput from "../../components/form/ItemSearchInput";
import Spinner from "../../components/misc/Spinner";
import { DEV_MODE } from "../../constants";
import {
  DOC_WIDGETS,
  EMPTY_DOC_SCHEMA,
  EMPTY_DOC_VALUES,
  applyDocWidget,
  canMoveDocColumnDown,
  canMoveDocColumnUp,
  docLeaves,
  getDocFieldWidget,
  makeDocField,
  makeField,
  makeGroup,
  moveDocColumn,
  nextColId,
  nextDocId,
} from "../delivery_notes/utils";
import { Role, useAuthStore } from "../../stores/auth";
import { useDeliveryNoteTemplatesStore } from "../../stores/delivery_note_templates";
import { useToastStore } from "../../stores/toast";

const createInitialValues = (template) => ({
  name: template?.name || "",
  items:
    template?.items?.map((item) => ({
      number: item.number,
      name: item.name,
      id: item.id,
      groups: item.groups || [],
    })) || [],
  documentation_schema: template?.documentation_schema || EMPTY_DOC_SCHEMA,
  documentation: template?.documentation || EMPTY_DOC_VALUES,
});

const groupItems = (items) => {
  const groupMap = new Map();
  const ungrouped = [];

  items.forEach((item, flatIndex) => {
    const group = item.groups?.[0];
    if (group) {
      const key = group.id;
      if (!groupMap.has(key)) {
        groupMap.set(key, { id: key, name: group.name, items: [] });
      }
      groupMap.get(key).items.push({ ...item, flatIndex });
    } else {
      ungrouped.push({ ...item, flatIndex });
    }
  });

  const groups = [...groupMap.values()];
  if (ungrouped.length > 0) {
    groups.push({ id: null, name: null, items: ungrouped });
  }
  return groups;
};

const createValidationSchema = (t) =>
  Yup.object().shape({
    name: Yup.string().required(t("form.errors.required", { attr: t("delivery_notes.templates.name") })),
  });

function RowActions({ onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const { t } = useTranslation();
  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-danger me-1"
        onClick={onRemove}
        aria-label={t("common.remove")}
      >
        <i className="fa fa-times" />
      </button>
      <button
        type="button"
        className="btn btn-sm btn-default me-1"
        disabled={isFirst}
        onClick={onMoveUp}
        aria-label={t("common.move_up")}
      >
        <i className="fa fa-arrow-up" />
      </button>
      <button
        type="button"
        className="btn btn-sm btn-default"
        disabled={isLast}
        onClick={onMoveDown}
        aria-label={t("common.move_down")}
      >
        <i className="fa fa-arrow-down" />
      </button>
    </>
  );
}

function ItemsCard({ items, setFieldValue }) {
  const { t } = useTranslation();

  const moveItem = (flatIndex, direction) => {
    const currentGroups = groupItems(items);

    for (const group of currentGroups) {
      const posIdx = group.items.findIndex((i) => i.flatIndex === flatIndex);
      if (posIdx === -1) continue;

      const targetPosIdx = posIdx + direction;
      if (targetPosIdx < 0 || targetPosIdx >= group.items.length) return;

      [group.items[posIdx], group.items[targetPosIdx]] = [group.items[targetPosIdx], group.items[posIdx]];

      const newItems = currentGroups.flatMap((g) => g.items.map(({ flatIndex: _, ...item }) => item));
      setFieldValue("items", newItems);
      return;
    }
  };

  const moveGroup = (groupIdx, direction) => {
    const currentGroups = groupItems(items);
    const targetIdx = groupIdx + direction;
    if (targetIdx < 0 || targetIdx >= currentGroups.length) return;

    const newGroups = [...currentGroups];
    [newGroups[groupIdx], newGroups[targetIdx]] = [newGroups[targetIdx], newGroups[groupIdx]];

    const newItems = newGroups.flatMap((g) => g.items.map(({ flatIndex, ...item }) => item));
    setFieldValue("items", newItems);
  };

  const removeItem = (index) => {
    setFieldValue(
      "items",
      items.filter((_, i) => i !== index),
    );
  };

  const addItem = (item) => {
    const newItem = {
      number: item.number,
      name: item.name,
      id: item.id,
      groups: item.groups || [],
    };
    const group = newItem.groups?.[0];

    if (group) {
      // Insert after the last item of the same group
      const lastGroupIndex = items.reduce((last, curr, i) => (curr.groups?.[0]?.id === group.id ? i : last), -1);
      if (lastGroupIndex >= 0) {
        const newItems = [...items];
        newItems.splice(lastGroupIndex + 1, 0, newItem);
        setFieldValue("items", newItems);
        return;
      }
    }

    setFieldValue("items", [...items, newItem]);
  };

  const existingNumbers = items.map((i) => i.number);
  const groups = groupItems(items);

  return (
    <div className="card border-0 mb-4">
      <div className="card-header h6 mb-0 bg-none p-3">
        <i className="fa fa-gear fa-lg fa-fw text-dark text-opacity-50 me-1"></i> {t("delivery_notes.templates.items")}
      </div>
      <div className="card-body">
        <div className="table-responsive mb-3">
          <table className="table table-hover table-panel align-middle mb-0">
            <thead>
              <tr>
                <th width={1}>{t("common.position")}</th>
                <th width={1}>{t("common.number_short")}</th>
                <th>{t("items.name")}</th>
                <th width={1}></th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group, groupIdx) => (
                <React.Fragment key={group.id ?? "ungrouped"}>
                  <tr>
                    <td colSpan={3} className="fw-bold bg-light text-muted py-2 ps-3">
                      <i className="fa fa-folder-open fa-fw me-1"></i>
                      {group.name || t("items.groups.ungrouped")}
                    </td>
                    <td className="bg-light text-nowrap text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-default me-1"
                        disabled={groupIdx === 0}
                        onClick={() => moveGroup(groupIdx, -1)}
                      >
                        <i className="fa fa-arrow-up" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-default"
                        disabled={groupIdx === groups.length - 1}
                        onClick={() => moveGroup(groupIdx, 1)}
                      >
                        <i className="fa fa-arrow-down" />
                      </button>
                    </td>
                  </tr>
                  {group.items.map((item, posIndex) => {
                    const isFirst = posIndex === 0;
                    const isLast = posIndex === group.items.length - 1;
                    return (
                      <tr key={`${item.number}-${item.flatIndex}`}>
                        <td className="text-center">{posIndex + 1}</td>
                        <td>{item.number}</td>
                        <td>{item.name}</td>
                        <td className="text-nowrap">
                          <RowActions
                            onRemove={() => removeItem(item.flatIndex)}
                            onMoveUp={() => moveItem(item.flatIndex, -1)}
                            onMoveDown={() => moveItem(item.flatIndex, 1)}
                            isFirst={isFirst}
                            isLast={isLast}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-3">
                    {t("delivery_notes.templates.no_items")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <ItemSearchInput
          onAdd={addItem}
          existingNumbers={existingNumbers}
          placeholder={t("delivery_notes.templates.search_item")}
        />
      </div>
    </div>
  );
}

function FieldEditor({ col, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const { t } = useTranslation();
  const widget = getDocFieldWidget(col);
  const enumValue = (col.enum || []).join(", ");
  const showEnum = widget === "select" || widget === "radio";
  const showUnit = widget === "number";

  return (
    <div className="border rounded p-2 mb-2 bg-light">
      <div className="d-flex align-items-start gap-2">
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: "140px" }}
          value={widget}
          onChange={(e) => onChange(applyDocWidget(col, e.target.value))}
        >
          {DOC_WIDGETS.map((w) => (
            <option key={w} value={w}>
              {t(`delivery_notes.templates.widgets.${w}`)}
            </option>
          ))}
        </select>
        <input
          type="text"
          className="form-control form-control-sm"
          value={col.title || ""}
          placeholder={t("delivery_notes.templates.col_name")}
          onChange={(e) => onChange({ ...col, title: e.target.value })}
        />
        <div className="text-nowrap ms-auto">
          <RowActions
            onRemove={onRemove}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            isFirst={isFirst}
            isLast={isLast}
          />
        </div>
      </div>
      {widget === "boolean" && (
        <textarea
          className="form-control form-control-sm mt-2"
          rows={3}
          value={col.description || ""}
          placeholder={t("delivery_notes.templates.field_description_placeholder")}
          onChange={(e) => onChange({ ...col, description: e.target.value })}
        />
      )}
      {(showEnum || showUnit) && (
        <div className="mt-2">
          {showEnum && (
            <input
              type="text"
              className="form-control form-control-sm mb-1"
              placeholder={t("delivery_notes.templates.choices_hint")}
              value={enumValue}
              onChange={(e) =>
                onChange({
                  ...col,
                  enum: e.target.value.split(",").map((s) => s.trim()),
                })
              }
            />
          )}
          {showUnit && (
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={t("delivery_notes.templates.field_unit")}
              value={col["x-unit"] || ""}
              onChange={(e) => onChange({ ...col, "x-unit": e.target.value || null })}
            />
          )}
        </div>
      )}
    </div>
  );
}

function GroupEditor({
  group,
  allColumns,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  onMoveChild,
  canMoveChildUp,
  canMoveChildDown,
  isFirst,
  isLast,
}) {
  const { t } = useTranslation();

  const addFieldToGroup = () => {
    const id = nextColId(allColumns);
    onChange({ ...group, columns: [...group.columns, makeField(id)] });
  };

  const updateChild = (idx, child) => {
    onChange({
      ...group,
      columns: group.columns.map((c, i) => (i === idx ? child : c)),
    });
  };

  const removeChild = (idx) => {
    onChange({ ...group, columns: group.columns.filter((_, i) => i !== idx) });
  };

  return (
    <div className="border rounded p-2 mb-2 bg-white">
      <div className="d-flex align-items-center gap-2 mb-2">
        <i className="fa fa-folder-open text-muted" />
        <input
          type="text"
          className="form-control form-control-sm"
          value={group.title || ""}
          placeholder={t("delivery_notes.templates.group_name")}
          onChange={(e) => onChange({ ...group, title: e.target.value })}
        />
        <button type="button" className="btn btn-sm btn-default" onClick={addFieldToGroup}>
          <i className="fa fa-plus me-1" />
          {t("delivery_notes.templates.add_field_to_group")}
        </button>
        <div className="text-nowrap ms-auto">
          <RowActions
            onRemove={onRemove}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            isFirst={isFirst}
            isLast={isLast}
          />
        </div>
      </div>
      <div className="ps-3">
        {group.columns.length === 0 ? (
          <div className="text-muted small py-2">{t("delivery_notes.templates.empty_group")}</div>
        ) : (
          group.columns.map((child, ci) => (
            <FieldEditor
              key={child.id}
              col={child}
              onChange={(next) => updateChild(ci, next)}
              onRemove={() => removeChild(ci)}
              onMoveUp={() => onMoveChild(ci, -1)}
              onMoveDown={() => onMoveChild(ci, 1)}
              isFirst={!canMoveChildUp(ci)}
              isLast={!canMoveChildDown(ci)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function DefaultValuesTable({ schema, values, setFieldValue }) {
  const { t } = useTranslation();
  const leaves = docLeaves(schema.columns);

  const addRow = () => {
    const blank = Object.fromEntries(
      leaves.map((l) => [l.id, getDocFieldWidget(l) === "boolean" ? false : ""]),
    );
    setFieldValue("documentation", { rows: [...values.rows, blank] });
  };

  const removeRow = (rowIndex) => {
    setFieldValue("documentation", {
      rows: values.rows.filter((_, i) => i !== rowIndex),
    });
  };

  const updateCell = (rowIndex, colId, value) => {
    setFieldValue("documentation", {
      rows: values.rows.map((row, ri) => (ri === rowIndex ? { ...row, [colId]: value } : row)),
    });
  };

  if (leaves.length === 0) return null;

  return (
    <div className="mt-3">
      <div className="d-flex align-items-center mb-2">
        <strong className="me-auto">{t("delivery_notes.templates.default_values")}</strong>
        <button type="button" className="btn btn-sm btn-default" onClick={addRow}>
          <i className="fa fa-plus me-1" />
          {t("delivery_notes.templates.add_row")}
        </button>
      </div>
      <div className="table-responsive">
        <table className="table table-bordered align-middle mb-0">
          <thead>
            <tr>
              {leaves.map((leaf) => (
                <th key={leaf.id} style={{ minWidth: "140px" }}>
                  {leaf.title || <em className="text-muted">{leaf.id}</em>}
                  {leaf["x-unit"] && (
                    <small className="text-muted ms-1">({leaf["x-unit"]})</small>
                  )}
                </th>
              ))}
              <th width={1}></th>
            </tr>
          </thead>
          <tbody>
            {values.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {leaves.map((leaf) => (
                  <td key={leaf.id}>
                    <DefaultCellInput
                      leaf={leaf}
                      value={row[leaf.id]}
                      onChange={(v) => updateCell(rowIndex, leaf.id, v)}
                    />
                  </td>
                ))}
                <td>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => removeRow(rowIndex)}>
                    <i className="fa fa-times" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DefaultCellInput({ leaf, value, onChange }) {
  const widget = getDocFieldWidget(leaf);
  if (widget === "boolean") {
    return (
      <input
        type="checkbox"
        className="form-check-input"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
      />
    );
  }
  if (widget === "select") {
    return (
      <select
        className="form-select form-select-sm"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      >
        {(leaf.enum || []).map((choice, i) => (
          <option key={i} value={choice}>
            {choice}
          </option>
        ))}
      </select>
    );
  }
  if (widget === "radio") {
    // Native radios cannot be unchecked, so clicking the selected choice again
    // resets the default to empty.
    const clearIfSelected = (choice) => {
      if (value === choice) onChange("");
    };

    return (
      <div className="d-flex flex-wrap gap-2">
        {(leaf.enum || []).map((choice, i) => (
          <label key={i} className="form-check form-check-inline mb-0">
            <input
              type="radio"
              className="form-check-input"
              name={`default-${leaf.id}`}
              checked={value === choice}
              onChange={() => onChange(choice)}
              onClick={() => clearIfSelected(choice)}
            />
            <span className="ms-1">{choice}</span>
          </label>
        ))}
      </div>
    );
  }
  if (widget === "number") {
    return (
      <input
        type="number"
        className="form-control form-control-sm"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      />
    );
  }
  if (widget === "date") {
    return (
      <input
        type="date"
        className="form-control form-control-sm"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <input
      type="text"
      className="form-control form-control-sm"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function DocumentationCard({ documentationSchema, documentation, setFieldValue }) {
  const { t } = useTranslation();
  const columns = documentationSchema?.columns || [];
  const documentColumns = documentationSchema?.document_columns || [];

  const updateSchema = (next) => setFieldValue("documentation_schema", next);

  const addField = () => {
    const id = nextColId(columns);
    updateSchema({ ...documentationSchema, columns: [...columns, makeField(id)] });
  };

  const addGroup = () => {
    updateSchema({ ...documentationSchema, columns: [...columns, makeGroup()] });
  };

  const updateColumn = (idx, next) => {
    updateSchema({
      ...documentationSchema,
      columns: columns.map((c, i) => (i === idx ? next : c)),
    });
  };

  const removeColumn = (idx) => {
    updateSchema({
      ...documentationSchema,
      columns: columns.filter((_, i) => i !== idx),
    });
  };

  const moveColumn = (idx, direction) => {
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= columns.length) return;
    const next = [...columns];
    [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
    updateSchema({ ...documentationSchema, columns: next });
  };

  const moveField = (path, direction) => {
    updateSchema({
      ...documentationSchema,
      columns: moveDocColumn(columns, path, direction),
    });
  };

  const addDocumentField = () => {
    const id = nextDocId(documentColumns);
    updateSchema({
      ...documentationSchema,
      document_columns: [...documentColumns, makeDocField(id)],
    });
  };

  const updateDocumentField = (idx, next) => {
    updateSchema({
      ...documentationSchema,
      document_columns: documentColumns.map((c, i) => (i === idx ? next : c)),
    });
  };

  const removeDocumentField = (idx) => {
    updateSchema({
      ...documentationSchema,
      document_columns: documentColumns.filter((_, i) => i !== idx),
    });
  };

  const moveDocumentField = (idx, direction) => {
    updateSchema({
      ...documentationSchema,
      document_columns: moveDocColumn(documentColumns, { idx }, direction),
    });
  };

  return (
    <div className="card border-0 mb-4">
      <div className="card-header h6 mb-0 bg-none p-3 d-flex align-items-center">
        <div>
          <i className="fa fa-table fa-lg fa-fw text-dark text-opacity-50 me-1"></i> {t("menu.documentation")}
        </div>
        <div className="ms-auto d-flex gap-1">
          <button type="button" className="btn btn-sm btn-default" onClick={addField}>
            <i className="fa fa-plus me-1" />
            {t("delivery_notes.templates.add_field")}
          </button>
          <button type="button" className="btn btn-sm btn-default" onClick={addGroup}>
            <i className="fa fa-plus me-1" />
            {t("delivery_notes.templates.add_group")}
          </button>
        </div>
      </div>
      <div className="card-body">
        <div className="mb-3">
          <label className="form-label small text-muted">
            {t("delivery_notes.templates.row_label")}
          </label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={documentationSchema?.row_label || ""}
            placeholder="Standort, Pos., …"
            onChange={(e) => updateSchema({ ...documentationSchema, row_label: e.target.value })}
          />
        </div>
        {columns.length === 0 ? (
          <div className="text-center text-muted py-3">
            {t("delivery_notes.templates.no_documentation")}
          </div>
        ) : (
          columns.map((col, idx) =>
            col.kind === "group" ? (
              <GroupEditor
                key={`g-${idx}`}
                group={col}
                allColumns={columns}
                onChange={(next) => updateColumn(idx, next)}
                onRemove={() => removeColumn(idx)}
                onMoveUp={() => moveColumn(idx, -1)}
                onMoveDown={() => moveColumn(idx, 1)}
                onMoveChild={(childIdx, direction) =>
                  moveField({ groupIdx: idx, idx: childIdx }, direction)
                }
                canMoveChildUp={(childIdx) =>
                  canMoveDocColumnUp(columns, { groupIdx: idx, idx: childIdx })
                }
                canMoveChildDown={(childIdx) =>
                  canMoveDocColumnDown(columns, { groupIdx: idx, idx: childIdx })
                }
                isFirst={idx === 0}
                isLast={idx === columns.length - 1}
              />
            ) : (
              <FieldEditor
                key={col.id}
                col={col}
                onChange={(next) => updateColumn(idx, next)}
                onRemove={() => removeColumn(idx)}
                onMoveUp={() => moveField({ idx }, -1)}
                onMoveDown={() => moveField({ idx }, 1)}
                isFirst={!canMoveDocColumnUp(columns, { idx })}
                isLast={!canMoveDocColumnDown(columns, { idx })}
              />
            ),
          )
        )}
        <DefaultValuesTable
          schema={documentationSchema}
          values={documentation}
          setFieldValue={setFieldValue}
        />

        <div className="mt-4">
          <div className="d-flex align-items-center mb-2">
            <strong className="me-auto">{t("delivery_notes.templates.document_fields")}</strong>
            <button type="button" className="btn btn-sm btn-default" onClick={addDocumentField}>
              <i className="fa fa-plus me-1" />
              {t("delivery_notes.templates.add_document_field")}
            </button>
          </div>
          {documentColumns.length === 0 ? (
            <div className="text-muted small py-2">
              {t("delivery_notes.templates.no_document_fields")}
            </div>
          ) : (
            documentColumns.map((col, idx) => (
              <FieldEditor
                key={col.id}
                col={col}
                onChange={(next) => updateDocumentField(idx, next)}
                onRemove={() => removeDocumentField(idx)}
                onMoveUp={() => moveDocumentField(idx, -1)}
                onMoveDown={() => moveDocumentField(idx, 1)}
                isFirst={idx === 0}
                isLast={idx === documentColumns.length - 1}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function DeliveryNoteTemplateAddEdit() {
  const { t } = useTranslation();
  const params = useParams();
  const templateId = Number.parseInt(params.templateId);
  const isAddMode = !templateId;
  const navigate = useNavigate();
  const addToast = useToastStore((state) => state.addToast);

  const [hasRole] = useAuthStore((state) => [state.hasRole], shallow);
  const canEdit = hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER);

  const [byIds, getById, createOne, updateOne] = useDeliveryNoteTemplatesStore(
    (state) => [state.byIds, state.getById, state.createOne, state.updateOne],
    shallow,
  );

  const template = Number.isInteger(templateId) ? byIds[templateId] : null;

  useEffect(() => {
    if (templateId) {
      getById({ id: templateId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const handleSubmit = async (values, { setErrors }) => {
    const payload = {
      name: values.name,
      item_ids: values.items.map((i) => i.id),
      documentation_schema: values.documentation_schema,
      documentation: values.documentation,
    };

    const { error } = isAddMode ? await createOne(payload) : await updateOne({ id: templateId, ...payload });

    if (error) {
      const msg = error?.detail?.[0]?.t || error?.detail;
      addToast("error", msg ? t(msg) : JSON.stringify(error));
    } else {
      addToast("success", t("common.saved"));
    }
  };

  if (Number.isInteger(templateId) && !template) {
    return <Spinner />;
  }

  if (!canEdit) {
    navigate("/");
    return null;
  }

  return (
    <div>
      <Breadcrumb
        items={[
          { label: t("delivery_notes.templates.templates"), to: "/delivery_note_templates" },
          { label: isAddMode ? t("menu.add") : template.name },
        ]}
      />

      <Formik
        initialValues={createInitialValues(template)}
        validationSchema={createValidationSchema(t)}
        validateOnChange={true}
        validateOnBlur={false}
        enableReinitialize={false}
        validateOnMount={false}
        onSubmit={handleSubmit}
      >
        {({ values, errors, isSubmitting, setFieldValue }) => (
          <Form>
            <div className="row">
              <div className="col">
                <div className="card border-0 mb-4">
                  <div className="card-header h6 mb-0 bg-none p-3">
                    <i className="fa fa-copy fa-lg fa-fw text-dark text-opacity-50 me-1"></i>{" "}
                    {t("delivery_notes.templates.template")}
                  </div>
                  <div className="card-body">
                    <div className="row mb-3 gx-3">
                      <div className="col-md">
                        <label className="form-label" htmlFor="name">
                          {t("delivery_notes.templates.name")}
                        </label>
                        <Field component={Input} name="name" className="form-control fs-13px h-45px" />
                      </div>
                    </div>
                  </div>
                </div>

                <ItemsCard items={values.items} setFieldValue={setFieldValue} />

                <DocumentationCard
                  documentationSchema={values.documentation_schema}
                  documentation={values.documentation}
                  setFieldValue={setFieldValue}
                />

                <div className="card border-0 mb-4">
                  <div className="card-body">
                    <div className="btn btn-group d-flex">
                      <button className="btn btn-success w-100" disabled={isSubmitting}>
                        {t("menu.submit")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {DEV_MODE && (
              <pre
                className="m-t-xl"
                style={{
                  fontSize: "1rem",
                  padding: ".25rem .5rem",
                  overflowX: "scroll",
                }}
              >
                VALUES: {JSON.stringify(values, null, 2)}
                <br />
                ERRORS: {JSON.stringify(errors, null, 2)}
              </pre>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default DeliveryNoteTemplateAddEdit;
